/**
 * Metrics § Post & content type breakdown (§8.6, PRD Metrics §4; redesign § breakdown).
 *
 * Share of posts, average engagement and the median engagement multiplier per post type and per
 * content type — same data, now two kit table islands with room to breathe. `counted` is a server
 * discriminant (`true`/`false` for post types, `null` for content types, where counted-ness is
 * undefined) and drives the marker — the client never re-derives which types count.
 *
 * `engagementX` is the §8.6 performance figure of the row, envelope and all: the chip renders
 * `display`, tints from `shade` and explains itself from the server's tooltip (§12.5).
 */

import type { BreakdownRowDTO, TypeBreakdownData } from '@lw/shared';
import { MultiplierBadge, Num } from '../../../components';
import { formatInteger } from '../../../lib/format';

function BreakdownBlock({ title, rows }: { title: string; rows: BreakdownRowDTO[] }) {
  return (
    <div className="bd-sub">
      <div className="metrics-subhead">
        <h3 className="metrics-subtitle">{title}</h3>
      </div>
      <div className="card card--table">
        <div className="table-wrap">
          <table className="table bd-table">
            <thead>
              <tr>
                <th>Type</th>
                <th className="num">Posts</th>
                <th className="num">Share</th>
                <th className="num">Avg engagement</th>
                <th className="num">Engagement ×</th>
                <th>Counted</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <td>{row.label}</td>
                  <td className="num">{formatInteger(row.posts)}</td>
                  <td className="num">
                    <Num value={row.sharePct} />
                  </td>
                  <td className="num">
                    <Num value={row.avgEngagement} />
                  </td>
                  <td className="num">
                    <MultiplierBadge value={row.engagementX} />
                  </td>
                  <td>
                    {row.counted === null ? (
                      <span className="faint">—</span>
                    ) : row.counted ? (
                      <span className="tag">Counted</span>
                    ) : (
                      <span className="faint">Excluded</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function TypeBreakdownSection({ data }: { data: TypeBreakdownData }) {
  return (
    <>
      <p className="bd-note muted">
        Shares are of {formatInteger(data.totalPosts)} posts in range after display filters.
      </p>
      <BreakdownBlock title="By post type" rows={data.byPostType} />
      <BreakdownBlock title="By content type" rows={data.byContentType} />
    </>
  );
}

export default TypeBreakdownSection;
